import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Flex } from '@chakra-ui/react';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useSidebarNavMatchRoute } from 'components/hooks/useSidebarNavMatchRoute';
import { startOfDay } from 'date-fns';
import { capitalize, startCase } from 'lodash';
import { useSelector } from 'react-redux';
import { useSelectedView } from 'reducers/viewsSlice';
import { ObjectiveKRCell as ObjectiveKRCellResponsive } from 'screens/home/summary/components/Cards/Goals/components/ObjectiveKRCell';
import { okrsPageSelector } from 'selectors/okrsPage';
import { StyledTooltip } from 'styles/common';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { Delete, UnArchive } from 'assets/assetsV1/iconComponents';

import { OBJECTIVE_LEVEL_TAGS, OKR_TYPES } from 'constants/ProjectConstants';
import { modifyString, StringModifier } from 'constants/copyHelpers';
import { convertDateToString4, notNullOrUndefined } from 'constants/helper';
import { isMobileView } from 'constants/isMobileView';

import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { Collaborators } from '../AgGridTreeView/components/CommonTreeCell/Collabs';
import { ObjectiveKRCell } from '../AgGridTreeView/components/CommonTreeCell/ObjectiveKRCell';
import { Owners } from '../AgGridTreeView/components/CommonTreeCell/Owners';
import { OwnersAndCollaborators } from '../AgGridTreeView/components/CommonTreeCell/OwnersAndCollaborators';
import { CustomHeaderCell } from '../AgGridTreeView/components/CustomHeaderCell';
import { LoadingOverlayComponent } from '../AgGridTreeView/components/LoadingOverlayComponent';
import { postProcessPopup } from '../AgGridTreeView/utils';
import { EmptyBar } from '../Insights/RabbitGroupByIndividualInsightsTable';
import { ArchivedBy } from './ArchivedBy';
import { NoArchivedGoalsOverlayComponent } from './NoRowsOverlayComponent';
import { useArchivedOKRQuery } from './datahooks';

const ArchivedGoalsTable = ({
  gridStatePersistenceKey = null,
  selectedTimeline,
  gridRef,
  openUnarchiveConfirmationModal,
  openPermanentlyDeleteConfirmationModal,
}) => {
  const { selectedTimelines } = useSelector(okrsPageSelector);

  const timeline = checkNewNavEnabled() ? selectedTimelines?.[0] : selectedTimeline;

  const { data, isLoading, isError } = useArchivedOKRQuery({
    ...(timeline ?? {}),
  });

  const { module } = useSidebarNavMatchRoute();

  const { type } = useSelectedView() ?? {};

  const [rowData, setRowData] = useState(data);

  // using useState to save grid row data because without useState loading overlay of ag grid doesn't work. Ag grid is not able to correctly detect when we are loading data and when is our data empty
  useEffect(() => {
    setRowData(data);
  }, [data]);

  const { objectivesName, moduleName, keyResultsName } = useOrganisationSettings();

  const sideBar = useMemo(
    () => ({
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressColumnFilter: true,
            suppressColumnSelectAll: true,
            suppressColumnExpandAll: true,
          },
        },
      ],
      defaultToolPanel: '',
    }),
    []
  );

  const columnDefs = useMemo(
    () => [
      // we're using the auto group column by default!
      /*
        IMPORTANT: whenever adding a new column or removing a column
        please update CSV download function on src/screens/Team/Rabbit/Goals/Insights/index.js
        Right now it does some calculations based on the column index
        TODO: make it more robust and remove the dependency on column index
      */
      {
        colId: 'type',
        field: 'type',
        filter: 'agMultiColumnFilter',
        headerName: 'Type',
        sortable: true,
        hide: true,
        minWidth: 150,
        maxWidth: 150,
        valueGetter: (data) =>
          typeof data.data?.type === 'string'
            ? modifyString(
                data.data?.type === OKR_TYPES.OBJECTIVE ? objectivesName : keyResultsName,
                StringModifier.TITLE_CASE
              )
            : '',
        cellRenderer: (props) => {
          const isObjectiveOrKR = typeof props?.data?.type === 'string';

          return (
            <Flex height="100%" alignItems="center">
              <TextBodyMedium>
                {isObjectiveOrKR
                  ? modifyString(
                      props?.data?.type === OKR_TYPES.OBJECTIVE ? objectivesName : keyResultsName,
                      StringModifier.TITLE_CASE
                    )
                  : ''}
              </TextBodyMedium>
            </Flex>
          );
        },
        filterParams: {
          filters: [
            {
              filter: 'agSetColumnFilter',
              display: 'accordion',
              title: 'Filter by Type',
              filterParams: {
                // values: (params) => {
                //   console.log({ params });
                //   // return data.data?.toIds?.map(({ employeeFunction }) => `${employeeFunction}`);
                // },

                valueFormatter: ({ value }) => {
                  if (value) return value;
                },
                getValue: (data) =>
                  data.data?.type
                    ? modifyString(
                        data.data?.type === OKR_TYPES.OBJECTIVE ? objectivesName : keyResultsName,
                        StringModifier.TITLE_CASE
                      )
                    : '',
                cellRenderer: (params) => (
                  <StyledTooltip title={params?.value ?? ''}>
                    <p>{params?.value ?? '(Blanks)'}</p>
                  </StyledTooltip>
                ),
              },
            },
          ],
        },
      },
      {
        field: 'archivedBy',
        filter: 'agMultiColumnFilter',
        colId: 'archived-by',
        sortable: true,
        headerName: 'Archived By',
        minWidth: 150,
        maxWidth: 150,
        filterParams: {
          filters: [
            {
              filter: 'agSetColumnFilter',
              display: 'accordion',
              title: 'Filter by Function',
              filterParams: {
                // values: (params) => {
                //   console.log({ params });
                //   // return data.data?.toIds?.map(({ employeeFunction }) => `${employeeFunction}`);
                // },

                valueFormatter: ({ value }) => {
                  if (value) return value;
                },
                getValue: (data) =>
                  data.data?.toIds
                    ?.map(({ employeeFunction, ownerType }) => ownerType === 'OWNER' && employeeFunction)
                    ?.filter((v) => v),
                cellRenderer: (params) => (
                  <StyledTooltip title={params?.value ?? ''}>
                    <p>{params?.value ?? '(Blanks)'}</p>
                  </StyledTooltip>
                ),
              },
            },
            {
              filter: 'agSetColumnFilter',
              title: 'Filter by Name',
              display: 'accordion',
              filterParams: {
                applyMiniFilterWhileTyping: true,

                getValue: (data) =>
                  data.data?.toIds?.map(({ displayName, ownerType }) => (ownerType === 'OWNER' ? displayName : '')),
                cellRenderer: (params) => (
                  <StyledTooltip title={params?.value ?? ''}>
                    <p>{params?.value ?? '(Blanks)'}</p>
                  </StyledTooltip>
                ),
              },
            },
          ],
        },
        valueGetter: (data) => data.data?.archivedBy?.displayName,
        cellRenderer: ArchivedBy,
      },

      {
        // Make sure the field key or if valueGetter exists, they're being updated on data update else cell won't re-render
        // if you've other data in cell renderer
        field: 'archivedDate',
        headerName: 'Archived Date',
        colId: 'archivedDate',
        minWidth: 150,
        maxWidth: 150,
        sortable: true,
        filter: 'agDateColumnFilter',
        filterValueGetter: (params) =>
          params?.data?.archivedDate ? startOfDay(new Date(params?.data?.archivedDate)) : null,
        valueGetter: (params) => (params?.data?.archivedDate ? params?.data?.archivedDate : null),
        cellRenderer: ({ data }) => {
          const { archivedDate } = data ?? {};

          return (
            <Flex alignItems="center" justifyContent="center" h="100%" w="100%">
              {notNullOrUndefined(archivedDate) ? (
                <TextBodyMedium>{convertDateToString4(archivedDate)}</TextBodyMedium>
              ) : (
                <EmptyBar />
              )}
            </Flex>
          );
        },
      },

      {
        field: 'toIds',
        filter: 'agMultiColumnFilter',
        colId: 'table-owner',
        sortable: true,
        headerName: 'Owner',
        minWidth: 150,
        maxWidth: 150,
        filterParams: {
          filters: [
            {
              filter: 'agSetColumnFilter',
              display: 'accordion',
              title: 'Filter by Function',
              filterParams: {
                // values: (params) => {
                //   console.log({ params });
                //   // return data.data?.toIds?.map(({ employeeFunction }) => `${employeeFunction}`);
                // },

                valueFormatter: ({ value }) => {
                  if (value) return value;
                },
                getValue: (data) =>
                  data.data?.toIds
                    ?.map(({ employeeFunction, ownerType }) => ownerType === 'OWNER' && employeeFunction)
                    ?.filter((v) => v),
                cellRenderer: (params) => (
                  <StyledTooltip title={params?.value ?? ''}>
                    <p>{params?.value ?? '(Blanks)'}</p>
                  </StyledTooltip>
                ),
              },
            },
            {
              filter: 'agSetColumnFilter',
              title: 'Filter by Name',
              display: 'accordion',
              filterParams: {
                applyMiniFilterWhileTyping: true,

                getValue: (data) =>
                  data.data?.toIds?.map(({ displayName, ownerType }) => (ownerType === 'OWNER' ? displayName : '')),
                cellRenderer: (params) => (
                  <StyledTooltip title={params?.value ?? ''}>
                    <p>{params?.value ?? '(Blanks)'}</p>
                  </StyledTooltip>
                ),
              },
            },
          ],
        },
        valueGetter: (data) =>
          data.data?.toIds?.map(({ ownerType, displayName }) => ownerType === 'OWNER' && displayName),
        cellRenderer: Owners,
      },
      {
        field: 'toIds',
        filter: 'agMultiColumnFilter',
        sortable: true,
        headerName: 'Collaborators',
        colId: 'table-collaborators',
        hide: true,
        minWidth: 150,
        // maxWidth: 200,
        filterParams: {
          filters: [
            {
              filter: 'agSetColumnFilter',
              display: 'accordion',
              title: 'Filter by Function',
              filterParams: {
                valueFormatter: ({ value }) => {
                  if (value) return value;
                },
                getValue: (data) =>
                  data.data?.toIds
                    ?.map(({ employeeFunction, ownerType }) => ownerType === 'COLLABORATOR' && employeeFunction)
                    ?.filter((v) => v),
                cellRenderer: (params) => (
                  <StyledTooltip title={params?.value ?? ''}>
                    <p>{params?.value ?? '(Blanks)'}</p>
                  </StyledTooltip>
                ),
              },
            },
            {
              filter: 'agSetColumnFilter',
              display: 'accordion',
              title: 'Filter by Name',
              filterParams: {
                applyMiniFilterWhileTyping: true,
                valueFormatter: ({ value }) => {
                  if (value) return value;
                  return '(Blanks)';
                },
              },
            },
          ],
        },
        valueGetter: (data) =>
          data.data?.toIds?.map(({ displayName, ownerType }) => ownerType === 'COLLABORATOR' && displayName),
        cellRenderer: Collaborators,
      },
      {
        field: 'toIds',
        filter: 'agMultiColumnFilter',
        colId: 'table-team',
        hide: true,
        sortable: true,
        headerName: 'Team',
        minWidth: 150,
        // maxWidth: 200,
        filterParams: {
          filters: [
            {
              filter: 'agSetColumnFilter',
              display: 'accordion',
              title: 'Filter by Function',
              filterParams: {
                valueFormatter: ({ value }) => {
                  if (value) return value;
                },
                getValue: (data) =>
                  data.data?.toIds?.map(({ employeeFunction, ownerType }) => employeeFunction)?.filter((v) => v),
                cellRenderer: (params) => (
                  <StyledTooltip title={params?.value ?? ''}>
                    <p>{params?.value ?? '(Blanks)'}</p>
                  </StyledTooltip>
                ),
              },
            },
            {
              filter: 'agSetColumnFilter',
              display: 'accordion',
              title: 'Filter by Name',
              filterParams: {
                applyMiniFilterWhileTyping: true,
                valueFormatter: ({ value }) => {
                  if (value) return value;
                  return '(Blanks)';
                },
              },
            },
          ],
        },
        valueGetter: (data) => data.data?.toIds?.map(({ displayName }) => displayName),
        cellRenderer: OwnersAndCollaborators,
      },
    ],
    [module]
  );

  const columnDefsResponsive = useMemo(() => [], []);

  const defaultColDef = useMemo(
    () => ({
      flex: 1,
      menuTabs: ['filterMenuTab'],
      headerComponent: CustomHeaderCell,
    }),
    []
  );

  const autoGroupColumnDef = useMemo(
    () => ({
      headerName: modifyString(moduleName, StringModifier.PLURAL_TITLE_CASE),
      minWidth: 340,
      groupId: 'okrs',
      // pinned: 'left',
      lockPosition: 'left',
      // cellClass: 'locked-col',
      flex: 2,
      filter: 'agMultiColumnFilter',
      colId: 'table-goal-name',
      filterParams: {
        filters: [
          {
            filter: 'agSetColumnFilter',
            display: 'accordion',
            title: 'Filter by Level',
            filterParams: {
              values: OBJECTIVE_LEVEL_TAGS.map(({ value }) => value),
              // this is used to display the value in the filter menu
              valueFormatter: ({ value }) => capitalize(startCase(value)),
              // this is used to get the key which is used to filter the data
              getValue: (data) => data.data.objectiveType,
            },
          },
          {
            display: 'accordion',
            title: `Filter by ${moduleName} Title`,
            getValue: (data) => data.data.title,
            filterParams: {
              // copy pasta from ag grid.
              // not touching it for now
              textFormatter: (r) => {
                if (r === null) return null;
                return r
                  .toLowerCase()
                  .replace(/[àáâãäå]/g, 'a')
                  .replace(/æ/g, 'ae')
                  .replace(/ç/g, 'c')
                  .replace(/[èéêë]/g, 'e')
                  .replace(/[ìíîï]/g, 'i')
                  .replace(/ñ/g, 'n')
                  .replace(/[òóôõö]/g, 'o')
                  .replace(/œ/g, 'oe')
                  .replace(/[ùúûü]/g, 'u')
                  .replace(/[ýÿ]/g, 'y');
              },
              filterOptions: ['contains', 'notContains'],
              debounceMs: 200,
              suppressAndOrCondition: true,
            },
          },
          // {
          //   filter: 'agSetColumnFilter'
          // }
        ],
      },
      valueGetter: (data) => data.data ?? {},
      filterValueGetter: (data) => data.data.title ?? '',
      cellRendererParams: {
        suppressCount: true,
        innerRenderer: ObjectiveKRCell,
      },
      cellStyle: (params) => ({ overflow: 'visible' }),
      sortable: false,
      comparator: (value1, value2) =>
        String(value1).toLocaleLowerCase().localeCompare(String(value2).toLocaleLowerCase()),
      equals: (okr1, okr2) => {
        const isTitleUnchanged = okr1.title === okr2.title;
        const isVisibilityUnchanged = okr1.visibilityType === okr2.visibilityType;
        const ifGoalChildExistsUnchanged = okr1.ifGoalChildExists === okr2.ifGoalChildExists;
        const isProgressTypeUnchanged = okr1.progressType === okr2.progressType;

        return isTitleUnchanged && isVisibilityUnchanged && ifGoalChildExistsUnchanged && isProgressTypeUnchanged;
      },
      cellClass: 'objective-kr-cell-ag-grid-wrap',
      autoHeight: true,
      wrapText: true,
      chartDataType: 'excluded',
    }),
    []
  );

  const autoGroupColumnDefResponsive = {
    headerName: moduleName,
    minWidth: 250,
    flex: 1,
    filter: true,
    colId: 'table-goal-name',
    cellStyle: { height: 'auto' },
    menuTabs: [],
    cellRendererParams: {
      suppressCount: true,
      innerRenderer: ObjectiveKRCellResponsive,
    },

    //  hacky way to make sure that the auto group column is updated on the basis of the valueGetter
    valueGetter: (data) =>
      data?.data?.title + data?.data?.progressLevel + data?.data?.progress + data?.data?.avgProgressPercentage,
  };

  const getDataPath = useMemo(() => (data) => data.hierarchy, []);

  /*
    Row IDs are provided via the getRowId() callback. 
    This results in Row Selection being maintained across 
    Row Data changes (assuming the Row exists in both sets) 
    and the HTML is not redrawn from scratch, 
    resulting in a Row Animation (animateRows=true) of the rows.

    REFER: https://www.ag-grid.com/react-data-grid/data-update-row-data/#example-immutable-store---large-dataset

    NOTE: If you don't do this, on data change, the grid flashes and loses its state. i.e. expanded rows, scroll position, etc.
  */

  const getRowId = useCallback(({ data }) => data?.uuid, []);

  // const onFirstDataRendered = (params) => {
  //   params.api.sizeColumnsToFit();
  // };

  const getRowStyle = (params) => {
    if (params?.data?.unMasked) {
      return { cursor: 'pointer' };
    }
  };

  return (
    <AgGridTable
      key={gridStatePersistenceKey}
      ref={gridRef}
      /* filter persistence props start */
      useFilterModelPersistence
      gridStatePersistenceKey={gridStatePersistenceKey}
      /* filter persistence props end */
      rowData={rowData}
      sideBar={isMobileView() ? false : sideBar}
      columnDefs={isMobileView() ? columnDefsResponsive : columnDefs}
      /* @ts-ignore */
      defaultColDef={defaultColDef}
      /* @ts-ignore */
      autoGroupColumnDef={isMobileView() ? autoGroupColumnDefResponsive : autoGroupColumnDef}
      // rowClass="ag-row-hide-on-hover"
      treeData
      animateRows
      groupDefaultExpanded={-1}
      getDataPath={getDataPath}
      // domLayout="autoHeight"
      rowHeight={84}
      getRowStyle={getRowStyle}
      /* @ts-ignore */
      getRowId={getRowId}
      suppressRowTransform
      loadingOverlayComponent={LoadingOverlayComponent}
      noRowsOverlayComponent={NoArchivedGoalsOverlayComponent}
      // debounceVerticalScrollbar={true}
      tooltipShowDelay={500}
      postProcessPopup={postProcessPopup}
      suppressMenuHide // <- this is to force the menu icon to show
      pagination
      paginationAutoPageSize
      kebabMenuIsRowSpecific
      kebabMenuOptionsGenerator={(data) => {
        const isTopLevelOKR = data?.hierarchy?.length === 1;

        const toReturn = [
          {
            label: 'Delete Permanently',
            onClick: () => {
              openPermanentlyDeleteConfirmationModal({
                okrUuid: data.uuid,
                okrType: data.type,
              });
            },
            disabled: false,
            Icon: (props) => <Delete {...props} mr={1} />,
          },
          {
            label: 'Unarchive',
            onClick: () => {
              openUnarchiveConfirmationModal({
                okrUuid: data.uuid,
                okrType: data.type,
              });
            },
            disabled: !isTopLevelOKR,
            disabledReason: `To unarchive child ${modifyString(
              moduleName,
              StringModifier.PLURAL_SENTENCE_CASE
            )}, please unarchive the entire ${modifyString(
              moduleName,
              StringModifier.SENTENCE_CASE
            )} tree by unarchiving the parent goal`,
            Icon: (props) => <UnArchive {...props} mr={1} />,
          },
        ];

        toReturn.push();

        return toReturn;
      }}
    />
  );
};

export { ArchivedGoalsTable };
